import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ApiError,
  api,
  listarMarcas,
  type CasoListItem,
  type EstadoCaso,
  type Page,
  type PosesionResumen,
  type DatosCasa,
  type Asesor,
} from "@/lib/api";
import { ESTADO_CASO_PILL } from "@/lib/estadoCaso";
import { useAuth, canEdit } from "@/lib/auth";
import AyudaVista from "@/ui/AyudaVista";
import ActividadPill from "@/ui/ActividadPill";
import { SkeletonRows } from "@/ui/Skeleton";
import { useToast } from "@/ui/Toast";
import Modal from "@/ui/Modal";
import NuevoCasoModal from "@/components/NuevoCasoModal";
import { guardarRecorrido } from "@/lib/recorridoCasos";
import FiltrosActivos from "@/components/casos/FiltrosActivos";
import {
  EsperanBadge,
  PosesionBadge,
  ProveedorBadge,
  CriticoBadge,
  TicketProveedorBadge,
} from "@/components/casos/BadgesCaso";
import ImeisLista from "@/components/casos/ImeisLista";
import { ayudaCasos } from "@/components/ayuda/vistas/casos";
import CasosFiltros, {
  BuscadorCasos,
  type Vista,
  type ClienteFiltro,
  type FiltroKpi,
  type CasosFiltrosProps,
  type OrdenCasos,
} from "@/components/casos/CasosFiltros";


const PAGE_SIZE = 50;
// Chips de IMEI visibles por caso en la lista (el Dashboard usa 5 a ancho
// completo; aquí la celda comparte espacio con 8 columnas más).
const MAX_IMEIS_LISTA = 3;
// Al agrupar por cliente cargamos un set grande (sin paginar) para que los
// grupos queden completos. El backend topa page_size en 200.
const GROUP_SIZE = 200;

const FILTERS_KEY = "casos:filters:v1";

type PersistedFilters = {
  vista: Vista;
  estadoFilter: EstadoCaso | "todos";
  q: string;
  clienteFiltro: ClienteFiltro | null;
  agrupar: boolean;
  esperaMiRespuesta: boolean;
  posesionFilter: "todos" | "mios" | "nosotros" | "cliente" | "proveedor";
  soloCriticos: boolean;
  proveedorPendiente: boolean;
  asesorFiltro: { id: number; nombre: string } | null;
  marcaFiltro: string | null;
  orden?: OrdenCasos;
};

function loadFilters(): Partial<PersistedFilters> {
  try {
    const raw = localStorage.getItem(FILTERS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

// El localStorage viejo guardaba "asc"/"desc" (orden por creación).
// Traducimos ese valor al nuevo preset y caemos en "recientes" ante
// cualquier valor desconocido o ausente.
function normalizarOrden(v: unknown): OrdenCasos {
  if (v === "recientes" || v === "antiguos" || v === "estancados" || v === "movidos") {
    return v;
  }
  if (v === "asc") return "antiguos";
  return "recientes";
}

// Filtro "desde estadísticas": llega por la URL (click en un KPI del Panel).
// Match exacto normalizado en backend — el conteo del KPI cuadra con la
// suma de Activos + Histórico. No se persiste en localStorage (es un salto).
function filtroKpiDesdeUrl(): { kpi: FiltroKpi | null; cliente: ClienteFiltro | null } {
  const sp = new URLSearchParams(window.location.search);
  const kpi: FiltroKpi = {};
  for (const k of ["marca", "modelo", "tipo_detalle"] as const) {
    const v = sp.get(k);
    if (v) kpi[k] = v;
  }
  const cat = sp.get("categoria");
  if (cat) {
    kpi.categoria = cat;
    kpi.categoria_nombre = sp.get("categoria_nombre") || cat;
  }
  let cliente: ClienteFiltro | null = null;
  const cid = sp.get("cliente_id");
  if (cid && sp.get("cliente_nombre")) {
    cliente = { id: Number(cid), nombre: sp.get("cliente_nombre")! };
  }
  return { kpi: Object.keys(kpi).length ? kpi : null, cliente };
}

function fechaCol(c: CasoListItem, vista: Vista): string {
  if (vista === "activos") return new Date(c.created_at).toLocaleDateString();
  return c.closed_at ? new Date(c.closed_at).toLocaleDateString() : "-";
}

export default function Casos() {
  const user = useAuth((s) => s.user);
  const toast = useToast();
  const nav = useNavigate();

  const initial = loadFilters();
  const desdeUrl = filtroKpiDesdeUrl();
  const [filtroKpi, setFiltroKpi] = useState<FiltroKpi | null>(desdeUrl.kpi);
  const [vista, setVista] = useState<Vista>(
    desdeUrl.kpi || desdeUrl.cliente ? "activos" : (initial.vista ?? "activos"),
  );
  const [estadoFilter, setEstadoFilter] = useState<EstadoCaso | "todos">(
    initial.estadoFilter ?? "todos",
  );
  const [q, setQ] = useState(initial.q ?? "");
  const [clienteFiltro, setClienteFiltro] = useState<ClienteFiltro | null>(
    desdeUrl.cliente ?? (desdeUrl.kpi ? null : (initial.clienteFiltro ?? null)),
  );
  const [agrupar, setAgrupar] = useState<boolean>(initial.agrupar ?? false);
  // Filtro "solo los que esperan mi respuesta" (menciones pendientes mías).
  const [esperaMiRespuesta, setEsperaMiRespuesta] = useState<boolean>(
    initial.esperaMiRespuesta ?? false,
  );
  // Posesión: "quién tiene la pelota" (deriva del estado). Filtro de un eje.
  const posesionUrl = (() => {
    const v = new URLSearchParams(window.location.search).get("posesion");
    return v === "mios" || v === "nosotros" || v === "cliente" || v === "proveedor"
      ? v
      : null;
  })();
  const [posesionFilter, setPosesionFilter] = useState<
    "todos" | "mios" | "nosotros" | "cliente" | "proveedor"
  >(posesionUrl ?? initial.posesionFilter ?? "todos");
  const [posesionResumen, setPosesionResumen] = useState<PosesionResumen | null>(null);
  // Nombre de la empresa (marca propia) para la etiqueta del filtro "nuestra
  // posesión". Editable en Configuración → Correo y empresa → Datos de la empresa; llega por /auth/soporte.
  const [nombreEmpresa, setNombreEmpresa] = useState("Emprenet");
  // Filtro "solo casos con un equipo marcado CRÍTICO".
  const [soloCriticos, setSoloCriticos] = useState<boolean>(initial.soloCriticos ?? false);
  const [proveedorPendiente, setProveedorPendiente] = useState<boolean>(
    initial.proveedorPendiente ?? false,
  );
  const [asesorFiltro, setAsesorFiltro] = useState<{ id: number; nombre: string } | null>(
    initial.asesorFiltro ?? null,
  );
  const [asesores, setAsesores] = useState<Asesor[]>([]);
  const [marcaFiltro, setMarcaFiltro] = useState<string | null>(initial.marcaFiltro ?? null);
  const [marcas, setMarcas] = useState<string[]>([]);
  const [gruposExpandidos, setGruposExpandidos] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [orden, setOrden] = useState<OrdenCasos>(normalizarOrden(initial.orden));

  const [data, setData] = useState<Page<CasoListItem> | null>(null);
  const [loading, setLoading] = useState(true);
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [filtrosOpen, setFiltrosOpen] = useState(false);

  const [selectedIdx, setSelectedIdx] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);
  const rowRefs = useRef<Array<HTMLTableRowElement | null>>([]);
  // Auto-salto a Histórico una sola vez por filtro de estadísticas
  // (ver load()). Se rearma al cambiar de filtro KPI.
  const autoSwitchRef = useRef(false);
  // El efecto debounce de búsqueda NO debe disparar load() al montar (el
  // efecto principal ya hace la carga inicial); si lo hace, su closure viejo
  // de `vista` pisa el auto-salto a Histórico. Solo corre al cambiar `q`.
  const busquedaMontadaRef = useRef(false);

  async function load() {
    setLoading(true);
    let switching = false;
    try {
      const params = new URLSearchParams();
      if (vista === "activos") params.set("abiertos", "true");
      else params.set("abiertos", "false");
      if (estadoFilter !== "todos") params.set("estado", estadoFilter);
      if (q.trim()) params.set("q", q.trim());
      if (clienteFiltro) params.set("cliente_id", String(clienteFiltro.id));
      if (esperaMiRespuesta) params.set("espera_mi_respuesta", "1");
      // "Míos" filtra por persona asignada (param aparte); el resto, por estado.
      if (posesionFilter === "mios") {
        if (user) params.set("asignado_a_id", String(user.id));
      } else if (posesionFilter !== "todos") {
        params.set("posesion", posesionFilter);
      }
      if (soloCriticos) params.set("critico", "1");
      if (proveedorPendiente) params.set("proveedor_pendiente", "1");
      if (asesorFiltro) params.set("asesor_id", String(asesorFiltro.id));
      const marcaParam = marcaFiltro ?? filtroKpi?.marca;
      if (marcaParam) params.set("marca", marcaParam);
      if (filtroKpi?.modelo) params.set("modelo", filtroKpi.modelo);
      if (filtroKpi?.tipo_detalle) params.set("tipo_detalle", filtroKpi.tipo_detalle);
      if (filtroKpi?.categoria) params.set("categoria", filtroKpi.categoria);
      params.set("orden", orden);
      params.set("page", String(agrupar ? 1 : page));
      params.set("page_size", String(agrupar ? GROUP_SIZE : PAGE_SIZE));

      const result = await api.get<Page<CasoListItem>>(`/api/casos?${params.toString()}`);
      // El conteo del panel de estadísticas suma abiertos + cerrados, pero el
      // drill-down abre la vista "Activos". Si vienes de estadísticas y
      // "Activos" sale vacío, los casos de esa categoría están cerrados
      // (Histórico): saltamos solos para no aparentar que el filtro no jala.
      // Mantenemos loading=true durante el salto para que no parpadee.
      if (
        filtroKpi &&
        vista === "activos" &&
        result.total === 0 &&
        !autoSwitchRef.current
      ) {
        autoSwitchRef.current = true;
        switching = true;
        setVista("historico");
        return;
      }
      setData(result);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude cargar casos", msg);
    } finally {
      if (!switching) setLoading(false);
    }
  }

  useEffect(() => {
    setPage(1);
  }, [vista, estadoFilter, clienteFiltro, filtroKpi, esperaMiRespuesta, posesionFilter, soloCriticos, proveedorPendiente, asesorFiltro, marcaFiltro, orden]);

  // En Histórico los casos cerrados no tienen actividad viva: si venías de
  // Activos con un orden por actividad, cae al orden por fecha de creación.
  useEffect(() => {
    if (vista === "historico" && (orden === "estancados" || orden === "movidos")) {
      setOrden("recientes");
    }
  }, [vista, orden]);

  // Cada filtro KPI nuevo puede volver a auto-saltar a Histórico.
  useEffect(() => {
    autoSwitchRef.current = false;
  }, [filtroKpi]);

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vista, estadoFilter, clienteFiltro, filtroKpi, page, agrupar, esperaMiRespuesta, posesionFilter, soloCriticos, proveedorPendiente, asesorFiltro, marcaFiltro, orden]);

  useEffect(() => {
    if (!busquedaMontadaRef.current) {
      busquedaMontadaRef.current = true;
      return;
    }
    const id = setTimeout(() => {
      setPage(1);
      void load();
    }, 250);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  useEffect(() => {
    try {
      localStorage.setItem(
        FILTERS_KEY,
        JSON.stringify({ vista, estadoFilter, q, clienteFiltro, agrupar, esperaMiRespuesta, posesionFilter, soloCriticos, proveedorPendiente, asesorFiltro, marcaFiltro, orden }),
      );
    } catch {
      // localStorage lleno o bloqueado — ignorar silencioso
    }
  }, [vista, estadoFilter, q, clienteFiltro, agrupar, esperaMiRespuesta, posesionFilter, soloCriticos, proveedorPendiente, asesorFiltro, marcaFiltro, orden]);

  // Nombre de la empresa para la etiqueta del filtro de posesión (una vez).
  useEffect(() => {
    let vivo = true;
    api
      .get<DatosCasa>("/api/auth/soporte")
      .then((s) => {
        if (vivo && s.nombre_empresa) setNombreEmpresa(s.nombre_empresa);
      })
      .catch(() => {
        /* etiqueta no crítica: si falla, queda el default */
      });
    return () => {
      vivo = false;
    };
  }, []);

  // Cargar catálogo de marcas (una vez, para el filtro por marca).
  useEffect(() => {
    let vivo = true;
    listarMarcas()
      .then((lista) => {
        if (vivo) setMarcas(lista.filter((m) => m.activo).map((m) => m.nombre));
      })
      .catch(() => {
        /* no crítico */
      });
    return () => {
      vivo = false;
    };
  }, []);

  // Cargar catálogo de asesores (una vez, para el filtro del picker).
  useEffect(() => {
    let vivo = true;
    api
      .get<Asesor[]>("/api/asesores")
      .then((lista) => {
        if (vivo) setAsesores(lista.filter((a) => a.activo));
      })
      .catch(() => { /* no crítico */ });
    return () => { vivo = false; };
  }, []);

  // Contador de posesión (casos abiertos). Se recarga al volver a "activos" o
  // cuando cambia la data (un cambio de estado pudo mover la posesión).
  useEffect(() => {
    if (vista !== "activos") {
      setPosesionResumen(null);
      return;
    }
    let vivo = true;
    api
      .get<PosesionResumen>("/api/casos/posesion-resumen")
      .then((r) => {
        if (vivo) setPosesionResumen(r);
      })
      .catch(() => {
        /* contador no crítico: si falla, no se muestra */
      });
    return () => {
      vivo = false;
    };
  }, [vista, data]);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Grupos por cliente (solo cuando agrupar está on), ordenados por # de casos.
  const grupos = useMemo(() => {
    if (!agrupar) return null;
    const map = new Map<string, CasoListItem[]>();
    for (const c of items) {
      const k = c.cliente_nombre || "(sin cliente)";
      const arr = map.get(k);
      if (arr) arr.push(c);
      else map.set(k, [c]);
    }
    return Array.from(map.entries()).sort(
      (a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]),
    );
  }, [items, agrupar]);

  // Guardar el recorrido en el orden VISUAL (agrupado o plano): es lo que
  // el detalle del caso usa para «Anterior / Siguiente».
  useEffect(() => {
    const visual = grupos ? grupos.flatMap(([, cs]) => cs) : items;
    if (visual.length > 0) guardarRecorrido(visual.map((c) => c.folio));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, grupos]);

  function toggleGrupo(cliente: string) {
    setGruposExpandidos((prev) => {
      const next = new Set(prev);
      if (next.has(cliente)) next.delete(cliente);
      else next.add(cliente);
      return next;
    });
  }
  const todosExpandidos =
    !!grupos && grupos.length > 0 && grupos.every(([c]) => gruposExpandidos.has(c));
  function toggleTodosGrupos() {
    if (!grupos) return;
    setGruposExpandidos(todosExpandidos ? new Set() : new Set(grupos.map(([c]) => c)));
  }

  useEffect(() => {
    setSelectedIdx(0);
  }, [items.length, items[0]?.id]);

  useEffect(() => {
    rowRefs.current[selectedIdx]?.scrollIntoView({ block: "nearest" });
  }, [selectedIdx]);

  // Atajos de teclado (desktop, vista plana): j/k navegan, Enter abre, "/" busca.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement;
      const isTyping =
        ["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName) || t.isContentEditable;

      if (e.key === "/" && !isTyping) {
        e.preventDefault();
        searchRef.current?.focus();
        return;
      }

      if (nuevoOpen || isTyping || agrupar) return;
      if (items.length === 0) return;

      if (e.key === "j") {
        e.preventDefault();
        setSelectedIdx((i) => Math.min(items.length - 1, i + 1));
      } else if (e.key === "k") {
        e.preventDefault();
        setSelectedIdx((i) => Math.max(0, i - 1));
      } else if (e.key === "Enter") {
        const c = items[selectedIdx];
        if (c) {
          e.preventDefault();
          nav(`/casos/${c.folio}`);
        }
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [items, selectedIdx, nuevoOpen, nav, agrupar]);

  const parcial = agrupar && total > items.length;

  // Cuántos filtros están activos (para el badge mobile)
  const filtrosActivos = [
    estadoFilter !== "todos",
    q.trim() !== "",
    clienteFiltro !== null,
    agrupar,
    esperaMiRespuesta,
    soloCriticos,
    proveedorPendiente,
    asesorFiltro !== null,
    marcaFiltro !== null,
    posesionFilter !== "todos",
    orden !== "recientes",
  ].filter(Boolean).length;

  /** Deja la lista como recién entrado. La búsqueda NO se toca: está a la
   *  vista y se borra con su propia ✕ (borrarla desde aquí sorprende). */
  function limpiarFiltros() {
    setEstadoFilter("todos");
    setClienteFiltro(null);
    setAgrupar(false);
    setEsperaMiRespuesta(false);
    setSoloCriticos(false);
    setProveedorPendiente(false);
    setAsesorFiltro(null);
    setMarcaFiltro(null);
    setPosesionFilter("todos");
    setOrden("recientes");
    setFiltroKpi(null);
    nav("/casos", { replace: true });
  }

  const filtrosProps: CasosFiltrosProps = {
    vista, estadoFilter, setEstadoFilter, q, setQ, searchRef,
    clienteFiltro, setClienteFiltro, agrupar, setAgrupar,
    esperaMiRespuesta, setEsperaMiRespuesta, soloCriticos, setSoloCriticos,
    proveedorPendiente, setProveedorPendiente,
    asesorFiltro, setAsesorFiltro, asesores, filtroKpi,
    marcaFiltro, setMarcaFiltro, marcas,
    onClearFiltroKpi: () => { setFiltroKpi(null); nav("/casos", { replace: true }); },
    orden, setOrden,
    total, loading,
    posesionFilter, setPosesionFilter,
    posesionResumen, nombreEmpresa,
    showPosesion: vista === "activos" && posesionResumen !== null,
  };

  return (
    <div className="space-y-4">
      <div className="sticky top-14 z-20 -mt-6 pt-6 pb-3 bg-ink-900/95 backdrop-blur space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-xl font-semibold">Casos</h1>
            <AyudaVista titulo="Casos" {...ayudaCasos(nombreEmpresa)} />
          </div>
          <button
            onClick={() => setNuevoOpen(true)}
            className="btn-primary disabled:opacity-50 shrink-0"
            disabled={!canEdit(user)}
          >
            + Nuevo<span className="hidden sm:inline">&nbsp;caso</span>
          </button>
        </div>

        {/* Tabs Activos / Historico */}
        <div className="border-b border-ink-700 flex gap-1">
          {(["activos", "historico"] as Vista[]).map((v) => (
            <button
              key={v}
              onClick={() => setVista(v)}
              className={`px-4 py-2 text-sm border-b-2 -mb-px transition ${
                vista === v
                  ? "border-accent-500 text-accent-400"
                  : "border-transparent text-zinc-400 hover:text-white"
              }`}
            >
              {v === "activos" ? "Activos" : "Histórico"}
              {data && vista === v && <span className="ml-2 text-xs text-zinc-500">({total})</span>}
            </button>
          ))}
        </div>

        {/* Filtros desktop (ocultos en mobile) */}
        <div className="hidden md:block space-y-3">
          <CasosFiltros {...filtrosProps} />
        </div>
        {/* Barra de filtros en móvil. El buscador va AFUERA del sheet y las
            fichas de lo aplicado se quitan sin abrirlo: antes, poner o borrar
            un filtro costaba el mismo viaje completo (abrir → buscar el
            control → aplicar → cerrar). */}
        <div className="md:hidden space-y-2">
          <BuscadorCasos q={q} setQ={setQ} searchRef={searchRef} />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setFiltrosOpen(true)}
              className="btn-ghost text-sm flex items-center gap-1.5"
            >
              <span>Filtros</span>
              {filtrosActivos > 0 && (
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-accent-500/20 text-accent-300 text-[10px] font-semibold">
                  {filtrosActivos}
                </span>
              )}
            </button>
            <span className="text-xs text-zinc-500 ml-auto">
              {loading ? "Cargando..." : `${total} caso${total === 1 ? "" : "s"}`}
            </span>
          </div>
          <FiltrosActivos
            vista={vista}
            estadoFilter={estadoFilter}
            setEstadoFilter={setEstadoFilter}
            clienteFiltro={clienteFiltro}
            setClienteFiltro={setClienteFiltro}
            agrupar={agrupar}
            setAgrupar={setAgrupar}
            esperaMiRespuesta={esperaMiRespuesta}
            setEsperaMiRespuesta={setEsperaMiRespuesta}
            soloCriticos={soloCriticos}
            proveedorPendiente={proveedorPendiente}
            setProveedorPendiente={setProveedorPendiente}
            setSoloCriticos={setSoloCriticos}
            asesorFiltro={asesorFiltro}
            setAsesorFiltro={setAsesorFiltro}
            marcaFiltro={marcaFiltro}
            setMarcaFiltro={setMarcaFiltro}
            posesionFilter={posesionFilter}
            setPosesionFilter={setPosesionFilter}
            nombreEmpresa={nombreEmpresa}
            filtroKpi={filtroKpi}
            onClearFiltroKpi={filtrosProps.onClearFiltroKpi}
            orden={orden}
            setOrden={setOrden}
            onLimpiarTodo={limpiarFiltros}
          />
        </div>
      </div>

      {/* Lista */}
      {agrupar ? (
        <div className="card space-y-1">
          {loading && items.length === 0 ? (
            <SkeletonRows rows={6} cols={1} />
          ) : items.length === 0 ? (
            <p className="text-sm text-zinc-500">No hay casos con estos filtros.</p>
          ) : (
            <>
              {parcial && (
                <p className="text-xs text-amber-300/80 pb-1">
                  Mostrando los primeros {items.length} de {total}. Afina con estado o búsqueda
                  para ver el resto agrupado.
                </p>
              )}
              <div className="flex justify-end">
                <button onClick={toggleTodosGrupos} className="btn-ghost text-xs">
                  {todosExpandidos ? "Colapsar todo" : "Expandir todo"}
                </button>
              </div>
              {grupos!.map(([cliente, casos]) => {
                const abierto = gruposExpandidos.has(cliente);
                return (
                  <div key={cliente} className="border-b border-ink-700/60 last:border-0">
                    <button
                      type="button"
                      onClick={() => toggleGrupo(cliente)}
                      aria-expanded={abierto}
                      className="w-full flex items-center gap-2 py-2 px-2 -mx-2 text-left rounded hover:bg-ink-800/40"
                    >
                      <span className="w-3 shrink-0 text-xs text-zinc-500">{abierto ? "▾" : "▸"}</span>
                      <h3 className="flex-1 break-words line-clamp-2 text-xs uppercase tracking-wide text-accent-400 font-medium">
                        {cliente}
                      </h3>
                      <span className="shrink-0 text-xs text-zinc-500">
                        ({casos.length} caso{casos.length === 1 ? "" : "s"})
                      </span>
                    </button>
                    {abierto && (
                      <ul className="divide-y divide-ink-700/40 pb-2">
                        {casos.map((c) => (
                          <CasoCardItem key={c.id} c={c} vista={vista} ocultarCliente />
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>
      ) : (
        <div className="card overflow-x-auto">
          {loading && items.length === 0 ? (
            <SkeletonRows rows={6} cols={10} />
          ) : items.length === 0 ? (
            <p className="text-sm text-zinc-500">
              {vista === "activos"
                ? "No hay casos activos con estos filtros."
                : "No hay casos en el histórico con estos filtros."}
            </p>
          ) : (
            <>
              {/* Mobile: cards */}
              <ul className="xl:hidden divide-y divide-ink-700">
                {items.map((c) => (
                  <CasoCardItem key={c.id} c={c} vista={vista} onCliente={setClienteFiltro} />
                ))}
              </ul>

              {/* Desktop: tabla */}
              <table className="hidden xl:table w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-zinc-500 text-left">
                  <tr>
                    <th className="py-2 pr-4">Folio</th>
                    <th className="py-2 pr-4">Cliente</th>
                    <th className="py-2 pr-4">Estado</th>
                    <th className="py-2 pr-4">Actividad</th>
                    <th className="py-2 pr-4">Equipo</th>
                    <th className="py-2 pr-4">TE</th>
                    <th className="py-2 pr-4">IMEIs</th>
                    <th className="py-2 pr-4">Notas</th>
                    <th className="py-2 pr-4">Asignado</th>
                    <th className="py-2 pr-4">{vista === "activos" ? "Creado" : "Cerrado"}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-700">
                  {items.map((c, idx) => (
                    <tr
                      key={c.id}
                      ref={(el) => {
                        rowRefs.current[idx] = el;
                      }}
                      onClick={(e) => {
                        // Click en cualquier parte de la fila abre el caso.
                        // Respeta cmd/ctrl-click (nueva pestaña vía el folio) y
                        // la selección de texto (copiar TE/IMEI sin navegar).
                        if (e.metaKey || e.ctrlKey) return;
                        if (window.getSelection()?.toString()) return;
                        nav(`/casos/${c.folio}`);
                      }}
                      className={`hover:bg-ink-800/50 cursor-pointer ${
                        idx === selectedIdx ? "bg-ink-800/60 ring-1 ring-accent-500/40" : ""
                      }`}
                    >
                      <td className="py-2 pr-4 font-mono text-accent-400 whitespace-nowrap">
                        <Link to={`/casos/${c.folio}`} onClick={(e) => e.stopPropagation()}>{c.folio}</Link>
                      </td>
                      <td className="py-2 pr-4">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setClienteFiltro({ id: c.cliente_id, nombre: c.cliente_nombre });
                          }}
                          className="text-left hover:text-accent-400 hover:underline"
                        >
                          {c.cliente_nombre}
                        </button>
                      </td>
                      <td className="py-2 pr-4">
                        <div className="flex flex-wrap items-center gap-1">
                          <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border ${ESTADO_CASO_PILL[c.estado]}`}>
                            {c.estado}
                          </span>
                          <EsperanBadge n={c.menciones_pendientes} sinLeer={c.menciones_sin_leer} />
                          <PosesionBadge posesion={c.posesion} />
                          <CriticoBadge on={c.tiene_imei_critico} />
                        <ProveedorBadge desde={c.proveedor_respondio_at} />
                          <TicketProveedorBadge jimi={c.jimi_work_order_no} />
                          <TicketProveedorBadge queclink={c.queclink_ticket_id} />
                        </div>
                      </td>
                      <td className="py-2 pr-4">
                        {c.nivel_actividad ? (
                          <ActividadPill dias={c.dias_sin_actividad} nivel={c.nivel_actividad} />
                        ) : (
                          <span className="text-zinc-600">—</span>
                        )}
                      </td>
                      <td className="py-2 pr-4 text-zinc-300">
                        {c.marca || "-"} {c.modelo || ""}
                      </td>
                      <td className="py-2 pr-4 text-zinc-400 text-xs font-mono whitespace-nowrap">{c.ticket_externo || "-"}</td>
                      <td className="py-2 pr-4">
                        {c.imeis.length === 0 ? (
                          <span className="text-zinc-600">—</span>
                        ) : (
                          <ImeisLista imeis={c.imeis} max={MAX_IMEIS_LISTA} className="max-w-[280px]" />
                        )}
                      </td>
                      <td className="py-2 pr-4 text-zinc-400">{c.notas_count}</td>
                      <td className="py-2 pr-4 text-xs text-zinc-400">{c.asignado_a_nombre || "-"}</td>
                      <td className="py-2 pr-4 text-xs text-zinc-500">{fechaCol(c, vista)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* Paginacion (solo en vista plana) */}
      {!agrupar && totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-zinc-500">
            Página {page} de {totalPages} ({total} totales)
          </span>
          <div className="flex gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="btn-ghost text-xs disabled:opacity-30"
            >
              ← Anterior
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              className="btn-ghost text-xs disabled:opacity-30"
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}

      {/* Modal filtros mobile */}
      <Modal
        open={filtrosOpen}
        onOpenChange={setFiltrosOpen}
        title="Filtros"
        closeOnOutsideClick
        footer={
          <button
            type="button"
            onClick={() => setFiltrosOpen(false)}
            className="btn-primary w-full sm:w-auto"
          >
            Listo
          </button>
        }
      >
        <CasosFiltros {...filtrosProps} ocultarBusqueda />
      </Modal>

      {/* Al crear, ir DIRECTO al caso para trabajarlo (pedido de Joseph:
          con el orden por estancados/filtros persistidos, el caso nuevo
          quedaba enterrado en la lista y había que buscarlo). */}
      <NuevoCasoModal
        open={nuevoOpen}
        onClose={() => setNuevoOpen(false)}
        onCreated={(caso) => nav(`/casos/${caso.folio}`)}
      />
    </div>
  );
}

// Badge "te esperan" cuando el caso tiene menciones pendientes del usuario
// actual. Pill ámbar (mismo lenguaje visual que el ⏰ recordatorio de la
// bitácora). No renderiza nada si n === 0.
// Card de un caso (mobile y dentro de grupos). Si onCliente está, el nombre del
// cliente es un botón que filtra; con ocultarCliente no se muestra (ya estás
// dentro del grupo de ese cliente).
function CasoCardItem({
  c,
  vista,
  onCliente,
  ocultarCliente,
}: {
  c: CasoListItem;
  vista: Vista;
  onCliente?: (f: ClienteFiltro) => void;
  ocultarCliente?: boolean;
}) {
  return (
    <li className="py-2.5">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Link to={`/casos/${c.folio}`} className="font-mono text-accent-400 text-sm">
          {c.folio}
        </Link>
        <span
          className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border ${ESTADO_CASO_PILL[c.estado]}`}
        >
          {c.estado}
        </span>
        <ActividadPill dias={c.dias_sin_actividad} nivel={c.nivel_actividad} />
        <EsperanBadge n={c.menciones_pendientes} sinLeer={c.menciones_sin_leer} />
        <PosesionBadge posesion={c.posesion} />
        <CriticoBadge on={c.tiene_imei_critico} />
                        <ProveedorBadge desde={c.proveedor_respondio_at} />
        <TicketProveedorBadge jimi={c.jimi_work_order_no} />
        <TicketProveedorBadge queclink={c.queclink_ticket_id} />
      </div>
      {!ocultarCliente && onCliente && (
        <button
          type="button"
          onClick={() => onCliente({ id: c.cliente_id, nombre: c.cliente_nombre })}
          className="mt-0.5 block max-w-full break-words text-left text-sm text-zinc-200 hover:text-accent-400"
        >
          {c.cliente_nombre}
        </button>
      )}
      <Link to={`/casos/${c.folio}`} className="mt-1 block text-xs text-zinc-400">
        {c.marca || "-"} {c.modelo || ""}
        {c.ticket_externo ? ` · ${c.ticket_externo}` : ""}
      </Link>
      {/* Los IMEIs van FUERA del enlace: el «+N más» es un botón que los
          despliega, y un botón dentro de un <a> rompe la navegación. */}
      {c.imeis.length > 0 && (
        <div className="mt-1 flex items-start gap-1">
          <span className="mr-0.5 pt-0.5 text-[10px] text-zinc-500" aria-hidden>
            📟
          </span>
          <ImeisLista imeis={c.imeis} max={MAX_IMEIS_LISTA} />
        </div>
      )}
      <Link
        to={`/casos/${c.folio}`}
        className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-zinc-500"
      >
        <span>📝 {c.notas_count}</span>
        <span>{c.asignado_a_nombre || "Sin asignar"}</span>
        <span>{fechaCol(c, vista)}</span>
      </Link>
    </li>
  );
}

